import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import ConfirmDialog from '../components/ConfirmDialog';
import { userService, accessRightService, companyService, MODULE_NAMES } from '../services/adminApi';

const TABS = ['Create User', 'App Access', 'Company Access', 'Department Access'] as const;
type Tab = (typeof TABS)[number];

interface LookupItem {
  id: string;
  code: string;
  name: string;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await userService.lookupEmployees(search);
  return res.data.employees.map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function UserCreation() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('Create User');
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Tab 1
  const [employeeId, setEmployeeId] = useState('');
  const [employeeLabel, setEmployeeLabel] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [password, setPassword] = useState('');
  const [allowMobileLogin, setAllowMobileLogin] = useState(false);
  const [viewOtherSalary, setViewOtherSalary] = useState(false);
  const [lockUnlockAttendance, setLockUnlockAttendance] = useState(false);

  // Tab 2
  const [accessRights, setAccessRights] = useState<{ id: string; code: string }[]>([]);
  const [appAccess, setAppAccess] = useState<Record<string, string>>({}); // moduleName -> accessRightId

  // Tab 3
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

  // Tab 4
  const [departments, setDepartments] = useState<LookupItem[]>([]);
  const [designations, setDesignations] = useState<LookupItem[]>([]);
  const [branches, setBranches] = useState<LookupItem[]>([]);
  const [deptScope, setDeptScope] = useState<
    Record<string, { isSuperAdmin: boolean; isApprover: boolean; departmentIds: string[]; designationIds: string[]; branchIds: string[] }>
  >({});

  useEffect(() => {
    accessRightService.list().then((res) => setAccessRights(res.data.accessRights || []));
    companyService.list().then((res) => {
      setCompanies(res.data.companies || []);
      setSelectedCompanies(res.data.companies?.map((c: any) => c.id) || []);
    });
    api.get('/masters/departments').then((res) => setDepartments(res.data.departments || []));
    api.get('/masters/designations').then((res) => setDesignations(res.data.designations || []));
    api.get('/tms/branches').then((res) => setBranches(res.data.branches || []));
  }, []);

  const scopeFor = (moduleName: string) =>
    deptScope[moduleName] || { isSuperAdmin: false, isApprover: false, departmentIds: [], designationIds: [], branchIds: [] };

  const updateScope = (moduleName: string, patch: Partial<ReturnType<typeof scopeFor>>) => {
    setDeptScope((prev) => ({ ...prev, [moduleName]: { ...scopeFor(moduleName), ...patch } }));
  };

  const toggleInArray = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const accessCodeFor = (moduleName: string) => accessRights.find((r) => r.id === appAccess[moduleName])?.code;

  const tabValid = (t: Tab): boolean => {
    switch (t) {
      case 'Create User':
        return !!email && !!password && (!allowMobileLogin || !!mobileNumber);
      case 'App Access':
        return MODULE_NAMES.every((m) => !!appAccess[m]);
      case 'Company Access':
        return selectedCompanies.length > 0;
      case 'Department Access':
        return MODULE_NAMES.every((m) => {
          const code = accessCodeFor(m);
          if (!code || code === 'NO ACCESS') return true;
          const s = scopeFor(m);
          return (
            s.isSuperAdmin ||
            s.isApprover ||
            s.departmentIds.length > 0 ||
            s.designationIds.length > 0 ||
            s.branchIds.length > 0
          );
        });
      default:
        return true;
    }
  };

  const incompleteTabs = TABS.filter((t) => !tabValid(t));

  const handleCreateClick = () => {
    if (incompleteTabs.length > 0) {
      setTab(incompleteTabs[0]);
      return;
    }
    setConfirmOpen(true);
  };

  const performCreate = async () => {
    setSaving(true);
    try {
      await userService.create({
        employeeId: employeeId || null,
        name: employeeId ? null : name,
        email,
        mobileNumber: mobileNumber || null,
        password,
        allowMobileLogin,
        viewOtherSalary,
        lockUnlockAttendance,
        appAccess: MODULE_NAMES.map((m) => ({
          moduleName: m,
          accessType: accessRights.find((r) => r.id === appAccess[m])?.code || 'NO ACCESS',
          accessRightId: appAccess[m] || null,
        })),
        companyAccess: selectedCompanies,
        departmentAccess: MODULE_NAMES.map((m) => ({ moduleName: m, ...scopeFor(m) })),
      });
      setConfirmOpen(false);
      navigate('/admin/users');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Create User</h1>

      <div className="flex gap-4 border-b mb-6">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`pb-2 text-sm font-medium ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {t}
            {!tabValid(t) && <span className="text-red-600 ml-1">*</span>}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-6 max-w-3xl">
        {tab === 'Create User' && (
          <div className="space-y-4">
            <LookupSelect
              label="Select Employee (leave blank for NONE / external user)"
              value={employeeId}
              displayValue={employeeLabel}
              onChange={(value, opt) => {
                setEmployeeId(value);
                if (opt) {
                  setEmployeeLabel(opt.label);
                } else {
                  setEmployeeLabel('');
                }
              }}
              fetchOptions={fetchEmployeeOptions}
              placeholder="Search employee..."
            />
            {!employeeId && (
              <Input label="Name (for external / NONE user)" value={name} onChange={(e) => setName(e.target.value)} />
            )}
            <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <Input
              label="Mobile Number"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              required={allowMobileLogin}
            />
            <Input
              label="Initial Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={allowMobileLogin} onChange={(e) => setAllowMobileLogin(e.target.checked)} />
              Allow mobile number login (mobile number becomes mandatory)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={viewOtherSalary} onChange={(e) => setViewOtherSalary(e.target.checked)} />
              View other employees' basic salary
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={lockUnlockAttendance}
                onChange={(e) => setLockUnlockAttendance(e.target.checked)}
              />
              Lock/Unlock TMS attendance transactions
            </label>
          </div>
        )}

        {tab === 'App Access' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-2">Assign an Access Right per module (controls what the user can do).</p>
            {MODULE_NAMES.map((m) => (
              <div key={m} className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium w-40">{m}</span>
                <Select
                  value={appAccess[m] || ''}
                  onChange={(e) => setAppAccess({ ...appAccess, [m]: e.target.value })}
                  options={accessRights.map((r) => ({ value: r.id, label: r.code }))}
                  placeholder="NO ACCESS"
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        )}

        {tab === 'Company Access' && (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 mb-2">Select which companies this user has access to.</p>
            {companies.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedCompanies.includes(c.id)}
                  onChange={() => setSelectedCompanies((prev) => toggleInArray(prev, c.id))}
                />
                {c.name}
              </label>
            ))}
          </div>
        )}

        {tab === 'Department Access' && (
          <div className="space-y-6">
            <p className="text-sm text-gray-500">
              Controls which employee data this user can see per module — Is Super Admin sees everything, Is
              Approver makes them selectable as an approver, otherwise data is scoped to the selected
              departments/designations/branches.
            </p>
            {MODULE_NAMES.map((m) => {
              const scope = scopeFor(m);
              return (
                <div key={m} className="border rounded-lg p-4">
                  <p className="font-semibold text-sm mb-2">{m}</p>
                  <div className="flex gap-6 mb-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={scope.isSuperAdmin}
                        onChange={(e) => updateScope(m, { isSuperAdmin: e.target.checked })}
                      />
                      Is Super Admin
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={scope.isApprover}
                        onChange={(e) => updateScope(m, { isApprover: e.target.checked })}
                      />
                      Is Approver
                    </label>
                  </div>
                  {!scope.isSuperAdmin && (
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <p className="font-medium mb-1">Departments</p>
                        {departments.map((d) => (
                          <label key={d.id} className="flex items-center gap-1 mb-1">
                            <input
                              type="checkbox"
                              checked={scope.departmentIds.includes(d.id)}
                              onChange={() =>
                                updateScope(m, { departmentIds: toggleInArray(scope.departmentIds, d.id) })
                              }
                            />
                            {d.name}
                          </label>
                        ))}
                      </div>
                      <div>
                        <p className="font-medium mb-1">Designations</p>
                        {designations.map((d) => (
                          <label key={d.id} className="flex items-center gap-1 mb-1">
                            <input
                              type="checkbox"
                              checked={scope.designationIds.includes(d.id)}
                              onChange={() =>
                                updateScope(m, { designationIds: toggleInArray(scope.designationIds, d.id) })
                              }
                            />
                            {d.name}
                          </label>
                        ))}
                      </div>
                      <div>
                        <p className="font-medium mb-1">Branch</p>
                        {branches.map((b) => (
                          <label key={b.id} className="flex items-center gap-1 mb-1">
                            <input
                              type="checkbox"
                              checked={scope.branchIds.includes(b.id)}
                              onChange={() =>
                                updateScope(m, { branchIds: toggleInArray(scope.branchIds, b.id) })
                              }
                            />
                            {b.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex flex-col items-end gap-2">
          {incompleteTabs.length > 0 && (
            <p className="text-sm text-red-600">
              Finish all tabs before creating: {incompleteTabs.join(', ')}
            </p>
          )}
          <Button onClick={handleCreateClick} loading={saving} disabled={incompleteTabs.length > 0}>
            Create User
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Create User"
        message="Are you sure you want to create this user?"
        confirmLabel="Create"
        variant="primary"
        loading={saving}
        onConfirm={performCreate}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
